<script setup lang="ts">
import type { BlogPost } from '~~/shared/utils/blog'
import { readingTimeMinutes } from '~~/shared/utils/blog'

interface RelatedPost {
  slug: string
  title: string
  excerpt: string | null
  cover_image_url: string | null
  published_at: string | null
}

const route = useRoute()
const slug = computed(() => route.params.slug as string)

const { data, error } = await useAsyncData(
  () => `blog-post-${slug.value}`,
  () => $fetch<{ post: BlogPost, related: RelatedPost[] }>(`/api/blog/${slug.value}`)
)

if (error.value || !data.value?.post) {
  throw createError({ statusCode: 404, statusMessage: 'Post not found', fatal: true })
}

const post = computed(() => data.value!.post)
const related = computed(() => data.value!.related ?? [])
const readTime = computed(() => readingTimeMinutes(post.value.content))

const siteUrl = useRuntimeConfig().public.siteUrl
const canonical = computed(() => `${siteUrl}/blog/${post.value.slug}`)
const metaDescription = computed(() => post.value.meta_description || post.value.excerpt || `${post.value.title} on the mychar.gg blog.`)

useSeoMeta({
  title: () => post.value.title,
  titleTemplate: (t?: string | null) => (t ? `${t} | mychar.gg blog` : 'mychar.gg blog'),
  ogTitle: () => post.value.title,
  description: () => metaDescription.value,
  ogDescription: () => metaDescription.value,
  ogType: 'article',
  ogImage: () => post.value.cover_image_url || undefined,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [{ key: 'canonical', rel: 'canonical', href: () => canonical.value }]
})

// Article + BreadcrumbList schema; dates from the row. No author is set, so
// author and publisher both link to the sitewide mychar.gg Organization.
useSchemaOrg([
  defineArticle({
    headline: post.value.title,
    description: metaDescription.value,
    // Article requires an image; the sitewide og-image stands in for coverless posts
    image: post.value.cover_image_url || `${siteUrl}/og-image.png`,
    datePublished: post.value.published_at || undefined,
    dateModified: post.value.updated_at || undefined
  }),
  defineBreadcrumb({
    itemListElement: [
      { name: 'Blog', item: `${siteUrl}/blog` },
      { name: post.value.title, item: canonical.value }
    ]
  })
])

function formatDate(date?: string | null): string {
  if (!date) return ''
  return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}
</script>

<template>
  <div v-if="post">
    <UContainer class="max-w-3xl pb-12 pt-28 sm:pb-16 sm:pt-32">
      <UBreadcrumb
        :items="[{ label: 'Blog', to: '/blog' }, { label: post.title }]"
        class="mb-6"
      />

      <div
        v-if="post.tags.length"
        class="mb-4 flex flex-wrap gap-1.5"
      >
        <span
          v-for="tag in post.tags"
          :key="tag"
          class="rounded-full bg-primary-500/10 px-2.5 py-0.5 text-xs font-medium text-primary-400"
        >
          {{ tag }}
        </span>
      </div>

      <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-highlighted">
        {{ post.title }}
      </h1>
      <p
        v-if="post.excerpt"
        class="mt-3 text-lg text-muted"
      >
        {{ post.excerpt }}
      </p>

      <div class="mt-6 border-y border-default py-4">
        <PostByline
          :date="post.published_at"
          :reading-time="readTime"
        />
      </div>

      <NuxtImg
        v-if="post.cover_image_url"
        :src="post.cover_image_url"
        :alt="post.title"
        class="mt-8 w-full rounded-2xl border border-default"
        loading="eager"
      />

      <div class="prose prose-invert dark:prose-invert mt-8 max-w-none">
        <MDC :value="post.content" />
      </div>
    </UContainer>

    <UContainer
      v-if="related.length"
      class="max-w-5xl pb-24"
    >
      <USeparator class="mb-10" />
      <h2 class="mb-6 text-xl font-bold text-highlighted">
        Keep reading
      </h2>
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <NuxtLink
          v-for="r in related"
          :key="r.slug"
          :to="`/blog/${r.slug}`"
          class="group rounded-2xl border border-default bg-elevated/40 p-5 shadow-(--surface-shadow) transition-colors hover:border-accented"
        >
          <h3 class="font-semibold text-highlighted group-hover:text-primary-400 transition-colors">
            {{ r.title }}
          </h3>
          <p
            v-if="r.excerpt"
            class="mt-2 line-clamp-2 text-sm text-muted"
          >
            {{ r.excerpt }}
          </p>
          <p
            v-if="r.published_at"
            class="mt-3 text-xs text-dimmed"
          >
            {{ formatDate(r.published_at) }}
          </p>
        </NuxtLink>
      </div>
    </UContainer>
  </div>
</template>
